import { useCallback, useEffect, useRef, useState } from "react";
import { config } from "@/shared/config";
import type { UserAccountData } from "@/shared/types/account";
import { Sentry } from "@/shared/lib/sentry";

interface UseUserAccountDataOptions {
  user: unknown;
  getAccessToken: () => Promise<string>;
}

export function useUserAccountData({
  user,
  getAccessToken,
}: UseUserAccountDataOptions) {
  const [userData, setUserData] = useState<UserAccountData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refetchUserData = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!user) {
      if (!isMountedRef.current || requestId !== requestIdRef.current) return;
      setUserData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(config.meUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = (await response.json()) as UserAccountData;
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;
        setUserData(data);
      } else {
        console.error("Failed to fetch user data:", response.status);
        Sentry.captureMessage("useUserAccountData: /me returned non-OK", {
          level: "warning",
          tags: { area: "user-account-data" },
          extra: { status: response.status },
        });
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;
        setUserData(null);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      Sentry.captureException(error, {
        tags: { area: "user-account-data" },
      });
      if (!isMountedRef.current || requestId !== requestIdRef.current) return;
      setUserData(null);
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [getAccessToken, user]);

  useEffect(() => {
    refetchUserData();
  }, [refetchUserData]);

  return {
    userData,
    isLoading,
    setUserData,
    refetchUserData,
  };
}
